<template>
  <div
    id="core-availability"
    @refresh="processPropsAndRender"
    :style="varPrimaryColorStyle"
  >
    <div
      v-if="!isLoading && isAvailable"
      class="delivery-options container-fluid"
    >
      <countdown-banner
        v-if="displayCountdown"
        :class="{ 'no-zip-or-weather': requiresCountdownSpacing }"
        :is-available-today="isAvailableToday"
        :destination-florist-cutoff="destinationFloristCutoff"
        :displaying-cached-data="displayingCachedData"
      />
      <div :class="['row', { 'adjusted-countdown-spacing': requiresCountdownSpacing }]">
        <div
          v-if="displayWeatherStatement"
          class="col-12"
        >
          <weather-statement
            :statement-html="availability.weatherStatementHtml"
            :with-countdown="displayCountdown"
          />
        </div>
        <div
          v-if="displayZipCodeSection"
          class="col-12 zip-code"
        >
          <p>
            {{ i18n('zipCodeLabel') }}
            <input
              class="form-control"
              :value="zipCodeValue"
              readonly
            >
          </p>
        </div>
        <div
          v-if="displayZipCodeSection"
          class="col-12 change-zip-code"
        >
          <p>
            {{ i18n('changeZipCode.text') }}
            <button
              type="button"
              class="as-link"
              :style="colorStyle"
              @click="changeZipCode()"
            >
              {{ i18n('changeZipCode.buttonLabel') }}
            </button>
          </p>
        </div>
        <div
          v-if="displayFirstOption"
          class="col-12 first-option-cta"
        >
          <p>
            <button
              :value="firstOption.value"
              :data-label="firstOption.orderLabel"
              type="button"
              :style="colorStyle"
              class="as-arrows-link"
              @click="selectDate($event.currentTarget)"
            >{{ i18n('firstOption', { label: firstOption.orderLabel }) }}</button>
          </p>
        </div>
        <div
          v-if="displayHolidayUnavailableMessage"
          class="col-12 holiday-unavailable"
          :style="colorStyle"
        >
          <p v-html="availability.holidayUnavailableHtml" />
        </div>
        <flex-message
          v-if="displayFlexMessage"
          class="col-12 flex-message"
          :header="flexMessage.header"
          :subheader="flexMessage.subheader"
        />
      </div>
      <options-grid
        :delivery-options="gridDeliveryOptions"
        :configured-count="options.gridOptionsCount"
        @date-selected="selectDate"
      />
      <div class="row">
        <div
          v-if="displayDisclaimerText"
          class="col-12 disclaimer-text"
        >
          <p>{{ options.disclaimerText }}</p>
        </div>
        <options-select
          v-if="displayFutureDeliveryOptions"
          class="col-12"
          :label="i18n('futureOptions')"
          :delivery-options="futureDeliveryOptions"
          @date-selected="selectDate"
        />
      </div>
    </div>
    <no-results v-if="!isLoading && !isAvailable" />
    <loading v-if="isLoading" />
  </div>
</template>

<script>
import { createStore } from '@/store';
import { mapState, mapGetters } from 'vuex';
import CountdownBanner from '@/components/CountdownBanner';
import WeatherStatement from '@/components/WeatherStatement';
import FlexMessage from '@/components/FlexMessage';
import OptionsGrid from '@/components/OptionsGrid';
import OptionsSelect from '@/components/OptionsSelect';
import NoResults from '@/components/NoResults';
import Loading from '@/components/Loading';
import DeliveryEventsReporter from '@/models/deliveryEventsReporter';

import { ERROR_CODES } from '@/constants/no-results';
import { PARAM_KEYS } from '@/constants/parameters';

const GRID_OPTIONS_COUNT_DEFAULT = 30;
const DEFAULT_OPTIONS = {
  primaryColor: '#000000',
  gridOptionsCount: GRID_OPTIONS_COUNT_DEFAULT,
  hideCountdown: false,
  hideWeatherStatement: false,
  hideZipCode: false,
  hideFirstOption: false,
  hideHolidayUnavailableMessage: false,
  hideFlexMessage: false,
  disclaimerText: null,
  hideFutureDeliveryOptions: false,
};

export default {
  name: 'App',
  components: {
    CountdownBanner,
    WeatherStatement,
    FlexMessage,
    OptionsGrid,
    OptionsSelect,
    NoResults,
    Loading
  },
  props: {
    baseUrl: {
      type: String,
      required: true
    },
    [PARAM_KEYS.SITE_SLUG]: {
      type: String,
      required: true
    },
    [PARAM_KEYS.SKUS]: {
      type: String,
      required: true
    },
    [PARAM_KEYS.ZIP_CODE]: {
      type: String,
      required: true
    },
    [PARAM_KEYS.START_DATE]: {
      type: String,
      required: true
    },
    [PARAM_KEYS.END_DATE]: {
      type: String,
      required: true
    },
    [PARAM_KEYS.VARIATION_ID]: {
      type: Number,
      default: null
    },
    isFeeExempt: {
      type: Boolean,
      default: false
    },
    optionsJson: {
      type: String,
      default: function() {
        return JSON.stringify({});
      }
    }
  },
  data() {
    return {
      availability: null,
      destinationFloristCutoff: null,
      destinationUtcOffsetMinutes: null,
      deliveryOptions: [],
      deliveryEvents: [],
      displayingCachedData: false // TODO: FSO-2241 - sett when response is cached
    }
  },
  emits: [
    'fetch-start',
    'fetch-complete',
    'date-selected',
    'zip-code-change-requested'
  ],
  computed: {
    ...mapState({
      now: state => state.time.now,
      primaryColor: state => state.primaryColor,
      unavailableCode: state => state.unavailableCode,
      unavailableDefaultMessage: state => state.unavailableDefaultMessage,
      unavailableSkus: state => state.unavailableSkus,
      productAvailability: state => state.productAvailability,
      errorKey: state => state.error.key,
      errorServerResponse: state => state.error.serverResponse,
      errorDefaultMessage: state => state.error.defaultMessage,
      parameters: state => state.parameters
    }),
    ...mapGetters({
      isMissingZipCode: 'parameters/isMissingZipCode',
      isMissingRequiredParams: 'parameters/isMissingRequiredParams'
    }),
    isAvailable: {
      get() {
        return this.$store.state.isAvailable;
      },
      set(value) {
        this.$store.commit('setIsAvailable', value);
      }
    },
    isLoading: {
      get() {
        return this.$store.state.isLoading;
      },
      set(value) {
        this.$store.commit('setIsLoading', value);
      }
    },
    displayCountdown() {
      return !this.options.hideCountdown && this.isAvailableToday;
    },
    requiresCountdownSpacing() {
      return this.displayCountdown
        && !this.displayWeatherStatement
        && !this.displayZipCodeSection;
    },
    displayWeatherStatement() {
      return !this.options.hideWeatherStatement && !!this.availability.weatherStatementHtml;
    },
    displayZipCodeSection() {
      return !this.options.hideZipCode;
    },
    displayFirstOption() {
      return !this.options.hideFirstOption && !!this.firstOption;
    },
    displayHolidayUnavailableMessage() {
      return !this.options.hideHolidayUnavailableMessage && !!this.availability.holidayUnavailableHtml;
    },
    displayFlexMessage() {
      return !this.options.hideFlexMessage
        && this.hasFlexDeliveryOption
        && !this.isFeeExempt;
    },
    displayDisclaimerText() {
      return !!this.options.disclaimerText && this.options.disclaimerText.length;
    },
    displayFutureDeliveryOptions() {
      return !this.options.hideFutureDeliveryOptions
        && this.futureDeliveryOptions.length
    },
    zipCodeValue() {
      return this[PARAM_KEYS.ZIP_CODE];
    },
    gridDeliveryOptions() {
      return this.deliveryOptions.slice(0, this.options.gridOptionsCount);
    },
    futureDeliveryOptions() {
      return this.deliveryOptions.slice(this.options.gridOptionsCount).filter(o => o.isAvailable);
    },
    firstOption() {
      const firstAvailable = this.deliveryOptions.find(o => o.isAvailable);

      let flexOptionTargetDate = null;
      if (!!firstAvailable && firstAvailable.isFlex()) {
        const targetDateOfFirstAvailableFlex = this.deliveryOptions.find(o => o.isDate() && o.date === firstAvailable.date);
        if (!!targetDateOfFirstAvailableFlex && targetDateOfFirstAvailableFlex.isAvailable) {
          flexOptionTargetDate = targetDateOfFirstAvailableFlex;
        }
      }

      return flexOptionTargetDate || firstAvailable;
    },
    firstFlexOption() {
      return this.availability.firstFlexOption();
    },
    hasFlexDeliveryOption() {
      return !!this.firstFlexOption;
    },
    flexMessage() {
      return this.hasFlexDeliveryOption
        ? {
          header: this.firstFlexOption.flexCalendarHeaderText,
          subheader: this.firstFlexOption.flexCalendarSubheaderText
        }
        : null;
    },
    isAvailableToday() {
      // Today is available iff:
      //  * there's a florist cutoff returned
      //  * now is before the florist cutoff
      //  * there is an option for today
      //  * today's option is available
      const todaysOption = this.availability?.todaysDeliveryDate();
      return !!this.destinationFloristCutoff
        && this.now < new Date(this.destinationFloristCutoff)
        && todaysOption?.isAvailable;
    },
    options() {
      const passedOptions = JSON.parse(this.optionsJson);
      return { ...DEFAULT_OPTIONS, ...passedOptions };
    },
    colorStyle() {
      return {
        color: this.primaryColor,
      };
    },
    varPrimaryColorStyle() {
      return {
        "--waffle-primary-color": this.primaryColor,
      };
    },
    fetchResponseData() {
      return {
        isAvailable: this.isAvailable,
        zipCode: this[PARAM_KEYS.ZIP_CODE],
        noResultsCode: this.errorKey ?? this.unavailableCode,
        noResultsDefaultMessage: this.errorDefaultMessage ?? this.unavailableDefaultMessage,
        unavailableSkus: this.unavailableSkus,
        productAvailability: this.productAvailability,
        serverErrors: this.errorServerResponse,
        deliveryEvents: this.deliveryEvents
      };
    }
  },
  watch: {
    $props: {
      handler() {
        this.processPropsAndRender();
      },
      deep: true,
      immediate: false,
    },
  },
  beforeCreate: function() {
    this.$store = createStore();
  },
  created() {
    this.$store.commit('setBaseUrl', this.baseUrl);
    this.$store.dispatch('startOneMinuteInterval');
    this.processPropsAndRender();
  },
  methods: {
    i18n(key, values = null) {
      return this.$t(this.i18nPath(key), values);
    },
    i18nPath(key) {
      return `${key}`;
    },
    processPropsAndRender() {
      this.isLoading = true;
      this.$store.commit('setPrimaryColor', this.options.primaryColor);
      this.$store.commit('setIsFeeExempt', this.isFeeExempt);
      this.$store.commit('parameters/set', this.$props);
      this.$emit('fetch-start', this.parameters);
      this.$store.commit('error/reset');

      if (this.isMissingRequiredParams) {
        const key = this.isMissingZipCode
          ? ERROR_CODES.ZIP_CODE_BLANK
          : ERROR_CODES.REQUIRED_PARAMS_MISSING;
        this.setError(key);
      } else {
        this.fetchAvailability();
      }
    },
    markFetchComplete() {
      this.isLoading = false;
      this.$emit('fetch-complete', this.fetchResponseData);
    },
    setError(key, serverResponse = {}) {
      this.isAvailable = false;
      const error = {
        key: key,
        serverResponse: serverResponse,
        defaultMessage: this.i18n(`errors.${key}`)
      }
      this.$store.commit('error/set', error);
      this.markFetchComplete();
    },
    setResults(availability) {
      this.availability = availability;
      this.destinationFloristCutoff = availability.destinationFloristCutoff;
      this.destinationUtcOffsetMinutes = availability.destinationUtcOffsetMinutes;
      this.deliveryOptions = availability.deliveryOptions;

      if (availability.isSingleProduct) {
        const deliveryEventReporter = new DeliveryEventsReporter(availability, this.parameters[PARAM_KEYS.ZIP_CODE], this.parameters[PARAM_KEYS.SKUS][0]);
        this.deliveryEvents = deliveryEventReporter.events();
      } else {
        this.deliveryEvents = [];
      }

      this.markFetchComplete();
    },
    fetchAvailability() {
      const payload = {
        successCallback: this.setResults,
        errorCallback: this.setError
      };
      this.$store.dispatch('getAvailability', payload);
    },
    changeZipCode() {
      this.$emit('zip-code-change-requested');
    },
    selectDate(selection) {
      const apiOptionJson = JSON.stringify(this.deliveryOptions.find((o) => o.value === selection.value).apiOption);
      const dateAttributes = {
        value: selection.value,
        label: selection.dataset.label,
        option: apiOptionJson
      };

      this.$emit('date-selected', dateAttributes);
    }
  }
};
</script>

<style lang="scss">
#core-availability {
  position: relative;

  p {
    font-size: $font-size-14px;
    margin-bottom: 0.5rem;
    color: $text-color;
  }

  .form-control {
    display: inline-block;
    font-size: $font-size-14px;

    &:disabled,
    &[readonly] {
      background-color: $gray-200;
      opacity: 1;
    }
  }

  .error-message {
    margin-bottom: 1rem;
  }

  .zip-code {
    margin-bottom: 0.5rem;

    .form-control {
      max-width: 178px;
      height: 2rem;
    }
  }

  .row.adjusted-countdown-spacing {
    @include media-breakpoint-up($countdown-banner-visible-breakpoint) {
      min-height: 88px;
    }

    .first-option-cta,
    .holiday-unavailable,
    .flex-message {
      p {
        @include media-breakpoint-up($countdown-banner-visible-breakpoint) {
          $countdown-spacing: $countdown-banner-width + $countdown-banner-right-no-zip-or-weather;
          padding-right: $countdown-spacing;
        }
      }
    }
  }

  .first-option-cta {
    button {
      font-size: $font-size-20px;
      font-weight: bold;
    }
  }

  // We're expecting HTML to look somewhat like this:
  // <span>This product is not available for delivery for Valentine's Day. <a href='/path'><span>Shop flowers available for Valentine's Day delivery</span></a></span>
  // Note that we need the text inside the link to be in a <span> tag in order to theme it
  // and the HTML should not add the trailing double arrows to the link
  .holiday-unavailable {
    p {
      font-size: $font-size-12px;
      color: currentColor;
      font-weight: bold;

      a {
        @include with-right-double-arrow();
        display: inline-block;
        background-color: currentColor;
        padding: 0 0.3125rem;
        text-decoration: underline;
        text-decoration-color: $white;

        span,
        &:after {
          color: $white;
        }

        &:hover {
          text-decoration: none;
        }
      }
    }
  }

  .disclaimer-text {
    font-size: $font-size-14px;
    margin-bottom: 0.5rem;

    a {
      color: var(--waffle-primary-color);
      font-weight: bold;
      text-decoration: underline;

      &:hover {
        color: var(--waffle-primary-color);
        text-decoration: none;
      }
    }
  }

  ul,
  ol {
    padding-left: 1.25rem;
    margin-bottom: 0.5rem;
  }
}
</style>
